import { assertNever } from "@redotech/util/type";

/**
 * Default template used when a merchant has not configured `customCopy`.
 * Renders as e.g. "Get it by April 21 to 84101" — identical to the
 * historical hardcoded widget output.
 */
export const EDD_DEFAULT_TEMPLATE = "Get it by {delivery_date}{to_zip_code}";

export type EddTemplateVariable = "delivery_date" | "to_zip_code";

export type EddTemplateToken =
  | { type: "text"; value: string }
  | { type: "variable"; name: EddTemplateVariable };

const VARIABLE_PATTERN = /\{delivery_date\}|\{to_zip_code\}/g;

/**
 * Split a template into interleaved text and variable tokens. Unknown tokens
 * like `{foo}` are left as literal text so merchants see the raw string.
 */
export function tokenizeEddTemplate(template: string): EddTemplateToken[] {
  const tokens: EddTemplateToken[] = [];
  let lastIndex = 0;
  for (const match of template.matchAll(VARIABLE_PATTERN)) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      tokens.push({
        type: "text",
        value: template.slice(lastIndex, matchIndex),
      });
    }
    switch (match[0]) {
      case "{delivery_date}":
        tokens.push({ type: "variable", name: "delivery_date" });
        break;
      case "{to_zip_code}":
        tokens.push({ type: "variable", name: "to_zip_code" });
        break;
    }
    lastIndex = matchIndex + match[0].length;
  }
  if (lastIndex < template.length) {
    tokens.push({ type: "text", value: template.slice(lastIndex) });
  }
  return tokens;
}

/**
 * Resolve a template to a plain string. Used for non-JSX consumers (previews,
 * server-side rendering, tests). The widget itself tokenizes separately to
 * preserve the ZIP code click-to-edit affordance.
 *
 * - `{delivery_date}` → formatted delivery date string
 * - `{to_zip_code}` → " to <zip>" when a ZIP is known, empty string otherwise
 *
 * Collapses runs of whitespace and trims the result, so templates that leave
 * a stray space before `{to_zip_code}` render cleanly when ZIP is absent.
 */
export function resolveEddTemplate(
  template: string,
  values: { formattedDeliveryDate: string; zipCode: string | undefined },
): string {
  const toZipCode = values.zipCode ? ` to ${values.zipCode}` : "";
  const resolved = tokenizeEddTemplate(template)
    .map((token) => {
      if (token.type === "text") {
        return token.value;
      }
      switch (token.name) {
        case "delivery_date":
          return values.formattedDeliveryDate;
        case "to_zip_code":
          return toZipCode;
        default:
          assertNever(token.name);
      }
    })
    .join("");
  return resolved.replace(/\s+/g, " ").trim();
}
